import { useState } from 'react';
import { keepPreviousData } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import {
  useListInterviewInvites,
  useListInterviewTypes,
  useGetMe,
  resolveInterviewInviteSessionIds,
} from '@sara/api-client';
import type { SortingState } from '@tanstack/react-table';
import { Skeleton } from '@sara/ui/components/skeleton';
import { Button } from '@sara/ui/components/button';
import { toast } from '@sara/ui/components/sonner';
import { Loader2, RefreshCw } from 'lucide-react';
import { INTERVIEW_SORT_FIELDS, type InterviewSortField } from '../../../../router/tree';
import { canAccessAdmin } from '../../../../lib/admin-gate';
import { getApiErrorMessage } from '../../../../lib/api-error';
import { stashReplaySessionIds } from '../../../../lib/replay-handoff';
import { InterviewListToolbar } from '../../components/InterviewListToolbar';
import { InterviewListTable, InterviewListPagination } from '../../components/InterviewListTable';
import { FilterBar } from '../../components/FilterBar';
import type { InterviewListContainerProps } from './types';

function isSortField(id: string): id is InterviewSortField {
  return (INTERVIEW_SORT_FIELDS as readonly string[]).includes(id);
}

export function InterviewListContainerContent({
  orgId,
  search,
  onSearchChange,
  onRowClick,
}: InterviewListContainerProps) {
  const { data, isLoading, error, isFetching } = useListInterviewInvites(
    {
      orgId,
      status: search.status,
      candidateSearch: search.candidateSearch,
      externalUserIdSearch: search.externalUserIdSearch,
      interviewTypeVersionId: search.interviewTypeVersionId,
      createdAfter: search.createdAfter,
      createdBefore: search.createdBefore,
      expiresAfter: search.expiresAfter,
      expiresBefore: search.expiresBefore,
      completedNoResults: search.completedNoResults,
      sortBy: search.sortBy,
      sortOrder: search.sortOrder,
      page: search.page,
      pageSize: search.pageSize,
    },
    { query: { placeholderData: keepPreviousData } }
  );

  const { data: interviewTypes } = useListInterviewTypes({ orgId });
  const { data: me } = useGetMe();
  const navigate = useNavigate();
  // Staff-only, and only when the list is narrowed by a search/filter — we don't
  // want a one-click path to replay *every* session in the org.
  const hasSearchOrFilter = Boolean(
    search.candidateSearch ||
    search.externalUserIdSearch ||
    search.interviewTypeVersionId ||
    search.createdAfter ||
    search.createdBefore ||
    search.expiresAfter ||
    search.expiresBefore ||
    search.completedNoResults ||
    (search.status && search.status.length > 0)
  );
  const canReprocess = Boolean(me && canAccessAdmin(me)) && hasSearchOrFilter;
  const [isResolving, setIsResolving] = useState(false);

  if (error) throw error;

  // Resolve the *current filtered view* to concrete session ids server-side and
  // hand those exact ids to the bulk-replay tool — so it replays precisely what
  // the operator filtered to, not a broader org-wide set. Ids can be many, so
  // they go via sessionStorage rather than the URL.
  async function handleReplayMatching() {
    setIsResolving(true);
    try {
      const { sessionIds } = await resolveInterviewInviteSessionIds({
        orgId,
        status: search.status,
        candidateSearch: search.candidateSearch,
        externalUserIdSearch: search.externalUserIdSearch,
        interviewTypeVersionId: search.interviewTypeVersionId,
        createdAfter: search.createdAfter,
        createdBefore: search.createdBefore,
        expiresAfter: search.expiresAfter,
        expiresBefore: search.expiresBefore,
        completedNoResults: search.completedNoResults,
      });
      if (sessionIds.length === 0) {
        toast.info('No matching sessions to replay for these filters.');
        return;
      }
      stashReplaySessionIds(sessionIds);
      void navigate({ to: '/admin/bulk-replay/new', search: { handoff: 'sessions' } });
    } catch (err) {
      toast.error('Could not load matching sessions', { description: getApiErrorMessage(err) });
    } finally {
      setIsResolving(false);
    }
  }

  if (isLoading) {
    return (
      <div data-testid="interview-list-loading" className="flex min-h-0 flex-1 flex-col gap-6">
        <div className="flex flex-wrap gap-2">
          <Skeleton className="h-9 w-64 rounded-full" />
          <Skeleton className="h-7 w-20 rounded-full" />
          <Skeleton className="h-7 w-24 rounded-full" />
        </div>
        <div className="space-y-px border-y border-border/70">
          <Skeleton className="h-16 rounded-none" />
          <Skeleton className="h-16 rounded-none" />
          <Skeleton className="h-16 rounded-none" />
        </div>
      </div>
    );
  }

  const items = data?.items ?? [];
  const total = data?.total ?? 0;

  const sorting: SortingState = [{ id: search.sortBy, desc: search.sortOrder === 'desc' }];

  function handleSortingChange(updater: SortingState | ((prev: SortingState) => SortingState)) {
    const next = typeof updater === 'function' ? updater(sorting) : updater;
    if (next.length === 0) return;
    const { id, desc } = next[0];
    if (!isSortField(id)) {
      throw new Error(`Attempted to sort by non-sortable column '${id}'`);
    }
    onSearchChange({ sortBy: id, sortOrder: desc ? 'desc' : 'asc', page: 1 });
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-6">
      <div className="flex flex-wrap items-center gap-3">
        <InterviewListToolbar
          value={{ candidateSearch: search.candidateSearch }}
          onChange={(next) => onSearchChange({ ...next, page: 1 })}
        />
        <FilterBar
          search={search}
          interviewTypes={interviewTypes ?? []}
          onChange={(update) => onSearchChange(update)}
        />
        {canReprocess && (
          <Button
            type="button"
            variant="outline"
            shape="pill"
            size="sm"
            className="ml-auto h-7 gap-1.5 px-2.5 text-[12.5px]"
            onClick={handleReplayMatching}
            disabled={isResolving}
          >
            {isResolving ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={1.6} />
            ) : (
              <RefreshCw className="h-3.5 w-3.5" strokeWidth={1.6} />
            )}
            Replay Completed Sessions
          </Button>
        )}
      </div>

      <InterviewListTable
        items={items}
        sorting={sorting}
        onSortingChange={handleSortingChange}
        onRowClick={onRowClick}
        isFetching={isFetching}
        footer={
          <InterviewListPagination
            page={search.page}
            pageSize={search.pageSize}
            total={total}
            onPageChange={(page) => onSearchChange({ page })}
            onPageSizeChange={(pageSize) => onSearchChange({ pageSize, page: 1 })}
          />
        }
      />
    </div>
  );
}
